import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  VaLoadingIndicator,
  VaModal,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import recordEvent from 'platform/monitoring/record-event';
import {
  getClaims,
  markOnboardingModalShown,
  setOnboardingFlowAsSeen,
} from '../actions/index';
import {
  selectCanAccessClaims,
  selectIsEnrolledInVAHealthCare,
  selectOnboardingModal,
} from '../selectors';
import {
  ONBOARDING_MODAL_EVENTS,
  ONBOARDING_MODAL_STATUS,
  ONBOARDING_MODAL_TITLE,
  getOnboardingModalCloseLabel,
} from '../analytics';

const ALL_BENEFITS_LINK = {
  href: '/welcome-kit/#explore-vagov-to-learn-about-your-benefits',
  text: 'Learn more about your benefits',
};

export function buildOnboardingLinks({ hasClaims, isEnrolledInVAHealthCare }) {
  return [
    {
      href: hasClaims
        ? '/claim-or-appeal-status'
        : '/disability/how-to-file-claim',
      icon: 'description',
      text: hasClaims
        ? 'Check your disability claim status'
        : 'Learn how to file a VA disability claim',
    },
    {
      href: isEnrolledInVAHealthCare
        ? '/my-health'
        : '/health-care/how-to-apply',
      icon: 'medical_services',
      text: isEnrolledInVAHealthCare
        ? 'Manage your VA health care'
        : 'Learn how to apply for VA health care',
    },
    {
      href: '/education/how-to-apply/',
      icon: 'school',
      text: 'Learn about VA education and training benefits',
    },
  ];
}

export default function OnboardingModal({ dismiss }) {
  const dispatch = useDispatch();
  const isEnrolledInVAHealthCare = useSelector(selectIsEnrolledInVAHealthCare);
  const onboardingStore = useSelector(selectOnboardingModal);
  const hasClaims = onboardingStore?.claims?.length > 0;
  const canAccessClaims = useSelector(selectCanAccessClaims);
  const claimsLoading =
    canAccessClaims &&
    (onboardingStore?.claimsLoading ||
      onboardingStore?.claimsAvailability === undefined);
  const shownEventRecordedRef = useRef(!!onboardingStore?.hasShownModal);

  const onboardingLinks = useMemo(
    () =>
      buildOnboardingLinks({
        hasClaims,
        isEnrolledInVAHealthCare,
      }),
    [hasClaims, isEnrolledInVAHealthCare],
  );

  useEffect(() => {
    if (!onboardingStore?.hasShownModal) {
      dispatch(markOnboardingModalShown());
      dispatch(setOnboardingFlowAsSeen());
    }
  }, [dispatch, onboardingStore?.hasShownModal]);

  useEffect(() => {
    if (
      canAccessClaims &&
      onboardingStore?.claimsAvailability === undefined &&
      !onboardingStore?.claimsLoading
    ) {
      dispatch(getClaims());
    }
  }, [
    canAccessClaims,
    onboardingStore?.claimsAvailability,
    onboardingStore?.claimsLoading,
    dispatch,
  ]);

  const recordShownEvent = useCallback(links => {
    if (shownEventRecordedRef.current) return;
    shownEventRecordedRef.current = true;

    recordEvent({
      event: ONBOARDING_MODAL_EVENTS.SHOWN,
      'modal-title': ONBOARDING_MODAL_TITLE,
      'modal-status': ONBOARDING_MODAL_STATUS,
      'modal-primaryButtonText': links[0]?.text,
      'modal-secondaryButtonText': links[1]?.text,
      'modal-tertiaryButtonText': links[2]?.text,
    });
  }, []);

  useEffect(() => {
    if (!claimsLoading) {
      recordShownEvent(onboardingLinks);
    }
  }, [claimsLoading, onboardingLinks, recordShownEvent]);

  // The close button and footer link work while claims are loading. If the
  // user uses either before the links load, record shown first with empty
  // button text so the click/close isn't orphaned. Otherwise this is a no-op.
  const recordShownBeforeLinksLoad = () => recordShownEvent([]);

  const handleLinkClick = link => {
    recordShownBeforeLinksLoad();
    recordEvent({
      event: ONBOARDING_MODAL_EVENTS.LINK_CLICK,
      'modal-title': ONBOARDING_MODAL_TITLE,
      'modal-status': ONBOARDING_MODAL_STATUS,
      'modal-clickLabel': link.text,
    });
  };

  const handleClose = closeEvent => {
    recordShownBeforeLinksLoad();
    recordEvent({
      event: ONBOARDING_MODAL_EVENTS.CLOSED,
      'modal-title': ONBOARDING_MODAL_TITLE,
      'modal-status': ONBOARDING_MODAL_STATUS,
      'modal-clickLabel': getOnboardingModalCloseLabel(closeEvent),
    });
    dismiss();
  };

  return (
    <VaModal
      clickToClose
      cssClass="va-modal onboarding-modal"
      disableAnalytics
      uswds
      label={ONBOARDING_MODAL_TITLE}
      onCloseEvent={handleClose}
      visible
    >
      <div className="onboarding-modal__content">
        <h2 id="homepage-modal-label-title" className="onboarding-modal__title">
          What would you like
          <br />
          to do first?
        </h2>
        <ul className="onboarding-modal__actions">
          {claimsLoading ? (
            <li className="onboarding-modal__loading-indicator">
              <VaLoadingIndicator message="Loading your claims..." />
            </li>
          ) : (
            onboardingLinks.map((link, index) => (
              <li
                key={link.href}
                className={`onboarding-modal__action-item${
                  index === onboardingLinks.length - 1
                    ? ' onboarding-modal__action-item--last'
                    : ''
                }`}
              >
                <va-link
                  disable-analytics
                  href={link.href}
                  icon-name={link.icon}
                  icon-size={3}
                  text={link.text}
                  onClick={() => handleLinkClick(link)}
                />
              </li>
            ))
          )}
        </ul>
        <div className="onboarding-modal__footer">
          <p className="onboarding-modal__footer-label">More options</p>
          <va-link
            disable-analytics
            href={ALL_BENEFITS_LINK.href}
            text={ALL_BENEFITS_LINK.text}
            onClick={() => handleLinkClick(ALL_BENEFITS_LINK)}
          />
        </div>
      </div>
    </VaModal>
  );
}

OnboardingModal.propTypes = {
  dismiss: PropTypes.func.isRequired,
};
